/**
 * #649 — static maps: report def id (packages/report-defs `id`, e.g.
 * "score.competitor-score-sheet") → its i18n key, and a paramsSchema property
 * KEY (e.g. "showId") → its i18n key. Mirrors the pattern in
 * apps/scribe-fe/src/util/roleLabels.js (#648): the def supplies a stable CODE,
 * the FE maps code → literal key, so `t()` is never called with a key built
 * from the code itself (no `t(`reports.${id}`)` / `t(`reports.${paramKey}`)`).
 *
 * Source of truth for WHICH reports/params exist is packages/report-defs (each
 * def's `name` / `paramsSchema.*.title`) — these maps only cover the ones
 * scoreadmin-fe has a translated label for. A def/param not (yet) listed here
 * still renders its English `name`/`title` (the caller passes it as the
 * fallback) rather than blank or a raw id/key. Add a new report or param here
 * AND under `reports.names` / `reports.params` in locales/en/scoreadmin.json
 * together.
 */
export const REPORT_NAME_KEYS = {
  "score.competitor-score-sheet": "reports.names.competitorScoreSheet",
  "score.final-class": "reports.names.finalClass",
  "score.high-point": "reports.names.highPoint",
  "score.results-summary-day": "reports.names.resultsSummaryDay",
  "score.announcer-order-of-go": "reports.names.announcerOrderOfGo",
};

// paramsSchema property keys are reused verbatim (same meaning) across every
// report def that declares them — see the defs under packages/report-defs/src/score/.
export const REPORT_PARAM_KEYS = {
  showId: "reports.params.show",
  rideId: "reports.params.ride",
  classId: "reports.params.class",
  day: "reports.params.day",
  ringId: "reports.params.ring",
  includeComments: "reports.params.includeComments",
  dimension: "reports.params.dimension",
};

/**
 * Resolve a report def to its translated display name.
 * @param {Function} t - i18next translation function (useTranslation("scoreadmin"))
 * @param {string} id - report def id, e.g. "score.high-point"
 * @param {string} [fallbackName] - the def's English `name` (or a run's remembered name)
 * @returns {string}
 */
export function reportName(t, id, fallbackName) {
  const key = REPORT_NAME_KEYS[id];
  return key ? t(key) : fallbackName || id;
}

/**
 * Resolve a paramsSchema property key to its translated field label.
 * @param {Function} t
 * @param {string} paramKey - e.g. "showId"
 * @param {string} [fallbackLabel] - the schema's English `title` (or the raw key)
 * @returns {string}
 */
export function reportParamLabel(t, paramKey, fallbackLabel) {
  const key = REPORT_PARAM_KEYS[paramKey];
  return key ? t(key) : fallbackLabel || paramKey;
}
